import React from 'react';
import {
  Sparkles,
  Heart,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  Folder,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { Card } from '../ui';
import type { MorningPageInsightDto, InsightType } from '../../services';
import './InsightCard.css';

interface InsightCardProps {
  insight: MorningPageInsightDto;
  onClick?: (insight: MorningPageInsightDto) => void;
  showDate?: boolean;
  compact?: boolean;
}

const insightTypeConfig: Record<InsightType, {
  icon: React.FC<{ size?: number }>;
  label: string;
  color: string;
  bgColor: string;
}> = {
  Theme: {
    icon: Sparkles,
    label: 'Theme',
    color: 'var(--color-primary)',
    bgColor: 'var(--color-primary-alpha-10)',
  },
  Emotion: {
    icon: Heart,
    label: 'Emotion',
    color: 'var(--color-coral)',
    bgColor: 'rgba(255, 107, 107, 0.1)',
  },
  Pattern: {
    icon: TrendingUp,
    label: 'Pattern',
    color: 'var(--color-teal)',
    bgColor: 'rgba(0, 210, 211, 0.1)',
  },
  SelfSabotage: {
    icon: AlertTriangle,
    label: 'Self-Sabotage',
    color: 'var(--color-warning)',
    bgColor: 'var(--color-warning-light)',
  },
  Recommendation: {
    icon: Lightbulb,
    label: 'Recommendation',
    color: 'var(--color-lime)',
    bgColor: 'rgba(92, 216, 90, 0.1)',
  },
};

export const InsightCard: React.FC<InsightCardProps> = ({
  insight,
  onClick,
  showDate = false,
  compact = false,
}) => {
  const config = insightTypeConfig[insight.insightType] || insightTypeConfig.Theme;
  const Icon = config.icon;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const confidenceLevel = insight.confidence >= 0.8
    ? 'high'
    : insight.confidence >= 0.5
      ? 'medium'
      : 'low';

  return (
    <Card
      variant="default"
      padding={compact ? 'sm' : 'md'}
      className={`insight-card ${compact ? 'insight-card--compact' : ''} ${onClick ? 'insight-card--clickable' : ''}`}
      onClick={onClick ? () => onClick(insight) : undefined}
    >
      <div className="insight-card__header">
        <div
          className="insight-card__icon"
          style={{
            color: config.color,
            backgroundColor: config.bgColor
          }}
        >
          <Icon size={compact ? 16 : 20} />
        </div>
        <div className="insight-card__meta">
          <span
            className="insight-card__type"
            style={{ color: config.color }}
          >
            {config.label}
          </span>
          {showDate && (
            <span className="insight-card__date">
              <Calendar size={12} />
              {formatDate(insight.createdAt)}
            </span>
          )}
        </div>
        {insight.isActionable && (
          <span className="insight-card__actionable" title="Actionable insight">
            <Lightbulb size={14} />
          </span>
        )}
      </div>

      <h4 className="insight-card__title">{insight.title}</h4>

      {!compact && (
        <p className="insight-card__content">{insight.content}</p>
      )}

      <div className="insight-card__footer">
        <div className="insight-card__confidence">
          <span className={`insight-card__confidence-dot insight-card__confidence-dot--${confidenceLevel}`} />
          <span className="insight-card__confidence-text">
            {Math.round(insight.confidence * 100)}% confidence
          </span>
        </div>

        {insight.relatedProjectName && (
          <div className="insight-card__project">
            <Folder size={12} />
            <span>{insight.relatedProjectName}</span>
          </div>
        )}

        {onClick && (
          <ChevronRight size={16} className="insight-card__arrow" />
        )}
      </div>
    </Card>
  );
};

export default InsightCard;
